import React, { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { MagnifyingGlass, User, Buildings, Handshake, X, CheckCircle } from "@phosphor-icons/react";

/**
 * Small inline HubSpot contact/company picker.
 * Props:
 *  - selected: {type: 'contact'|'company', id, label, subtitle} | null
 *  - onSelect: (record) => void  (called when a result is picked)
 *  - onClear: () => void
 *  - onAutofill: ({ client_legal_name, recipient_name, recipient_designation }) => void
 */
export default function HubSpotContactPicker({ selected, onSelect, onClear, onAutofill }) {
  const [enabled, setEnabled] = useState(null); // null = loading, true/false
  const [tab, setTab] = useState("contacts");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    api.get("/integrations/hubspot").then(({ data }) => setEnabled(!!data?.configured)).catch(() => setEnabled(false));
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    if (!query.trim() || !enabled) { setResults([]); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setBusy(true);
      try {
        const { data } = await api.post("/integrations/hubspot/search", { object_type: tab, query: query.trim() });
        setResults(data?.results || []);
        setOpen(true);
      } catch (e) {
        const d = e?.response?.data?.detail;
        toast.error(typeof d === "string" ? d : (d?.message || "HubSpot search failed"));
      } finally { setBusy(false); }
    }, 350);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query, tab, enabled]);

  const pickContact = (r) => {
    const p = r.properties || {};
    const fullName = [p.firstname, p.lastname].filter(Boolean).join(" ").trim();
    const label = fullName || p.email || `Contact #${r.id}`;
    const subtitle = [p.email, p.company, p.jobtitle].filter(Boolean).join(" · ");
    onSelect({ type: "contact", id: r.id, label, subtitle });
    onAutofill && onAutofill({
      client_legal_name: p.company || "",
      recipient_name: fullName,
      recipient_designation: p.jobtitle || "",
    });
    setOpen(false);
    setQuery("");
    setResults([]);
  };

  const pickCompany = (r) => {
    const p = r.properties || {};
    const label = p.name || `Company #${r.id}`;
    const subtitle = [p.domain, p.industry, p.city].filter(Boolean).join(" · ");
    onSelect({ type: "company", id: r.id, label, subtitle });
    onAutofill && onAutofill({ client_legal_name: p.name || "" });
    setOpen(false);
    setQuery("");
    setResults([]);
  };

  const pickDeal = async (r) => {
    const p = r.properties || {};
    const label = p.dealname || `Deal #${r.id}`;
    const parts = [];
    if (p.amount) parts.push(`₹${Number(p.amount).toLocaleString("en-IN")}`);
    if (p.dealstage) parts.push(p.dealstage);
    if (p.closedate) parts.push(`close ${String(p.closedate).slice(0,10)}`);
    onSelect({ type: "deal", id: r.id, label, subtitle: parts.join(" · ") });
    setOpen(false);
    setQuery("");
    setResults([]);
    // Fetch the deal's associated contact + company to autofill
    try {
      const { data } = await api.get(`/integrations/hubspot/deals/${r.id}`);
      const c = data?.contact?.properties || {};
      const co = data?.company?.properties || {};
      const fullName = [c.firstname, c.lastname].filter(Boolean).join(" ").trim();
      const autofill = {};
      const clientName = co.name || c.company || "";
      if (clientName) autofill.client_legal_name = clientName;
      if (fullName) autofill.recipient_name = fullName;
      if (c.jobtitle) autofill.recipient_designation = c.jobtitle;
      // Also cascade the associated contact/company IDs so proposal is fully linked
      if (data?.contact?.id) autofill.__contact_id = data.contact.id;
      if (data?.company?.id) autofill.__company_id = data.company.id;
      if (Object.keys(autofill).length && onAutofill) {
        onAutofill(autofill);
        const parts2 = [];
        if (clientName) parts2.push(clientName);
        if (fullName) parts2.push(fullName);
        if (parts2.length) toast.success(`Autofilled from HubSpot deal · ${parts2.join(" / ")}`);
      }
    } catch (e) {
      // Non-fatal — user can still fill fields manually
    }
  };

  if (enabled === null) return null;

  if (!enabled) {
    return (
      <div className="border border-dashed border-slate-300 rounded-sm p-3 text-xs text-slate-600" data-testid="hubspot-picker-disabled">
        HubSpot is not connected. Ask an admin to connect it in <b>Integrations</b> to pick contacts here.
      </div>
    );
  }

  return (
    <div className="border border-slate-200 rounded-sm bg-white p-3 relative" ref={boxRef} data-testid="hubspot-picker">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="label-micro">HubSpot lookup</div>
        <div className="inline-flex text-[11px] font-semibold border border-slate-300 rounded-sm overflow-hidden">
          <button type="button" onClick={() => setTab("contacts")} data-testid="hs-tab-contacts"
                  className={`px-2 py-1 ${tab === "contacts" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>Contacts</button>
          <button type="button" onClick={() => setTab("companies")} data-testid="hs-tab-companies"
                  className={`px-2 py-1 border-l border-slate-300 ${tab === "companies" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>Companies</button>
          <button type="button" onClick={() => setTab("deals")} data-testid="hs-tab-deals"
                  className={`px-2 py-1 border-l border-slate-300 ${tab === "deals" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>Deals</button>
        </div>
      </div>

      {selected ? (
        <div className="flex items-center justify-between gap-2 border border-emerald-300 bg-emerald-50 rounded-sm px-3 py-2" data-testid="hs-selected">
          <div className="flex items-center gap-2 min-w-0">
            <CheckCircle size={16} weight="fill" className="text-emerald-600 shrink-0"/>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-slate-900 truncate">{selected.label}</div>
              <div className="text-xs text-slate-600 truncate">{selected.type === "contact" ? "Contact" : "Company"} #{selected.id}{selected.subtitle ? ` · ${selected.subtitle}` : ""}</div>
            </div>
          </div>
          <button type="button" onClick={onClear} data-testid="hs-clear" className="text-xs text-red-600 inline-flex items-center gap-1 hover:underline">
            <X size={12}/> Clear
          </button>
        </div>
      ) : (
        <div className="relative">
          <div className="flex items-center gap-2 border border-slate-300 rounded-sm px-2 py-1.5">
            <MagnifyingGlass size={14} className="text-slate-500"/>
            <input value={query} onChange={(e) => setQuery(e.target.value)}
                   onFocus={() => query && setOpen(true)}
                   placeholder={tab === "contacts" ? "Search by name or email…" : tab === "companies" ? "Search by company name or domain…" : "Search by deal name…"}
                   data-testid="hs-search-input"
                   className="flex-1 text-sm outline-none bg-transparent"/>
            {busy && <span className="text-[10px] text-slate-400">Searching…</span>}
          </div>
          {open && results.length > 0 && (
            <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-sm shadow-lg z-30 max-h-72 overflow-y-auto" data-testid="hs-results">
              {results.map((r) => {
                const p = r.properties || {};
                if (tab === "contacts") {
                  const fullName = [p.firstname, p.lastname].filter(Boolean).join(" ").trim();
                  return (
                    <button key={r.id} type="button" onClick={() => pickContact(r)} data-testid={`hs-pick-contact-${r.id}`}
                            className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b border-slate-100 flex items-start gap-2">
                      <User size={16} className="text-blue-600 mt-0.5 shrink-0"/>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-slate-900 truncate">{fullName || p.email || `Contact #${r.id}`}</div>
                        <div className="text-xs text-slate-500 truncate">{[p.email, p.company, p.jobtitle].filter(Boolean).join(" · ") || "—"}</div>
                      </div>
                    </button>
                  );
                }
                if (tab === "companies") {
                  return (
                    <button key={r.id} type="button" onClick={() => pickCompany(r)} data-testid={`hs-pick-company-${r.id}`}
                            className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b border-slate-100 flex items-start gap-2">
                      <Buildings size={16} className="text-blue-600 mt-0.5 shrink-0"/>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-slate-900 truncate">{p.name || `Company #${r.id}`}</div>
                        <div className="text-xs text-slate-500 truncate">{[p.domain, p.industry, p.city].filter(Boolean).join(" · ") || "—"}</div>
                      </div>
                    </button>
                  );
                }
                // deals
                const parts = [];
                if (p.amount) parts.push(`₹${Number(p.amount).toLocaleString("en-IN")}`);
                if (p.dealstage) parts.push(p.dealstage);
                if (p.closedate) parts.push(`close ${String(p.closedate).slice(0,10)}`);
                return (
                  <button key={r.id} type="button" onClick={() => pickDeal(r)} data-testid={`hs-pick-deal-${r.id}`}
                          className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b border-slate-100 flex items-start gap-2">
                    <Handshake size={16} className="text-orange-600 mt-0.5 shrink-0"/>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-slate-900 truncate">{p.dealname || `Deal #${r.id}`}</div>
                      <div className="text-xs text-slate-500 truncate">{parts.join(" · ") || `Deal ID ${r.id}`}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
          {open && query && !busy && results.length === 0 && (
            <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-sm shadow-lg z-30 px-3 py-3 text-xs text-slate-500" data-testid="hs-empty">
              No {tab} found for "{query}"
            </div>
          )}
        </div>
      )}
    </div>
  );
}
